/**
 * Кэш списка переписок и правила его обновления по событиям.
 *
 * Список лежит в TanStack Query как «бесконечный» запрос: страницы по 25 штук. События
 * реального времени правят этот кэш на месте, без перезапроса (иначе список мигал бы
 * при каждом сообщении). Функции чистые: получают данные, отдают новые.
 */
import type { InfiniteData } from '@tanstack/react-query';
import {
  MESSAGE_ACTIVITY, MESSAGE_INCOMING, MESSAGE_OUTGOING, type CwMessage, type InboxConversation,
} from '@/api/inboxTypes';
import { PAGE_SIZE, matchesQuery, type ListQuery } from './inboxQuery';

export interface ListPage {
  conversations: InboxConversation[];
  /** Сколько всего переписок у запроса по счётчикам Chatwoot (null — не знаем). */
  total: number | null;
}

export type ListData = InfiniteData<ListPage, number>;

export type ConvPatch =
  /** Полный снимок переписки (события conversation.*, ответ show). */
  | { kind: 'snapshot'; conv: InboxConversation }
  /** Новое сообщение (message.created). */
  | { kind: 'message'; convId: number; message: CwMessage }
  /** Изменилось сообщение (message.updated): правим только если оно последнее. */
  | { kind: 'messageUpdated'; convId: number; message: CwMessage }
  | { kind: 'read'; convId: number }
  | { kind: 'remove'; convId: number };

export interface PatchResult {
  data: ListData;
  /** Переписки нет в кэше, а патч без снимка — её надо дозагрузить одним запросом. */
  unknown: boolean;
  changed: boolean;
  /** Переписка уже была в этом списке до патча. */
  found: boolean;
}

export function flatten(data: ListData | undefined): InboxConversation[] {
  if (!data) return [];
  const seen = new Set<number>();
  const out: InboxConversation[] = [];
  for (const page of data.pages) {
    for (const c of page.conversations) {
      if (seen.has(c.id)) continue;
      seen.add(c.id);
      out.push(c);
    }
  }
  return out;
}

/** Есть ли что догружать. */
export function hasNextPage(data: ListData | undefined): boolean {
  if (!data || data.pages.length === 0) return false;
  const last = data.pages[data.pages.length - 1];
  if (last.conversations.length < PAGE_SIZE) return false;
  return last.total === null || flatten(data).length < last.total;
}

function byActivity(a: InboxConversation, b: InboxConversation): number {
  return b.lastActivityAt - a.lastActivityAt || b.id - a.id;
}

/** Собрать страницы заново из плоского списка (порядок как у сервера, по 25). */
export function rebuild(list: InboxConversation[], total: number | null): ListData {
  const pages: ListPage[] = [];
  for (let i = 0; i < list.length; i += PAGE_SIZE) {
    pages.push({ conversations: list.slice(i, i + PAGE_SIZE), total });
  }
  if (pages.length === 0) pages.push({ conversations: [], total });
  return { pages, pageParams: pages.map((_, i) => i + 1) };
}

const newerMessage = (a: CwMessage | null, b: CwMessage | null): CwMessage | null =>
  !a ? b : !b ? a : b.id >= a.id ? b : a;

/** Применить сообщение к переписке (последняя реплика, непрочитанные, «клиент ждёт»). */
function withMessage(c: InboxConversation, m: CwMessage): InboxConversation {
  if (m.message_type === MESSAGE_ACTIVITY) {
    return { ...c, lastActivityAt: Math.max(c.lastActivityAt, m.created_at) };
  }
  const incoming = m.message_type === MESSAGE_INCOMING;
  const outgoingReply = m.message_type === MESSAGE_OUTGOING && !m.private && m.sender?.type !== 'agent_bot';
  return {
    ...c,
    lastMessage: newerMessage(c.lastMessage, m),
    lastActivityAt: m.conversation?.last_activity_at ?? Math.max(c.lastActivityAt, m.created_at),
    unreadCount: m.conversation?.unread_count ?? (incoming ? c.unreadCount + 1 : c.unreadCount),
    waitingSince: incoming ? c.waitingSince || m.created_at : outgoingReply ? 0 : c.waitingSince,
    firstReplyAt: outgoingReply && c.firstReplyAt === 0 ? m.created_at : c.firstReplyAt,
  };
}

export function mergeSnapshot(old: InboxConversation | undefined, next: InboxConversation): InboxConversation {
  if (!old) return next;
  return { ...next, lastMessage: newerMessage(old.lastMessage, next.lastMessage) };
}

/**
 * Применить патч к данным ОДНОГО списка (с учётом того, подходит ли переписка под его запрос).
 * myUserId нужен для папки «Мои».
 */
export function applyPatch(
  data: ListData,
  patch: ConvPatch,
  query: ListQuery,
  myUserId: number | null,
): PatchResult {
  const list = flatten(data);
  const total = data.pages[data.pages.length - 1]?.total ?? null;
  return applyPatchImpl(list, total, data, patch, query, myUserId);
}

function convIdOf(p: ConvPatch): number {
  return p.kind === 'snapshot' ? p.conv.id : p.convId;
}

function applyPatchImpl(
  list: InboxConversation[],
  total: number | null,
  data: ListData,
  patch: ConvPatch,
  query: ListQuery,
  myUserId: number | null,
): PatchResult {
  const id = convIdOf(patch);
  const at = list.findIndex((c) => c.id === id);
  const old = at >= 0 ? list[at] : undefined;
  const unchanged: PatchResult = { data, unknown: false, changed: false, found: !!old };

  if (patch.kind === 'remove') {
    if (!old) return unchanged;
    return commit(list.filter((c) => c.id !== id), total === null ? null : Math.max(0, total - 1), true);
  }

  let next: InboxConversation | undefined;
  switch (patch.kind) {
    case 'snapshot':
      next = mergeSnapshot(old, patch.conv);
      break;
    case 'message':
      if (!old) return { data, unknown: patch.message.message_type !== MESSAGE_ACTIVITY, changed: false, found: false };
      next = withMessage(old, patch.message);
      break;
    case 'messageUpdated':
      if (!old || old.lastMessage?.id !== patch.message.id) return unchanged;
      next = { ...old, lastMessage: { ...old.lastMessage, ...patch.message } };
      break;
    case 'read':
      if (!old || old.unreadCount === 0) return unchanged;
      next = { ...old, unreadCount: 0 };
      break;
  }

  const fits = matchesQuery(next, query, myUserId);
  if (!fits) {
    if (!old) return unchanged;
    return commit(list.filter((c) => c.id !== id), total === null ? null : Math.max(0, total - 1), true);
  }

  if (old) {
    const copy = list.slice();
    copy[at] = next;
    // Порядок «по активности» меняется только у сортировки по давности.
    return commit(query.sort === 'activity' ? copy.sort(byActivity) : copy, total, true);
  }

  // Новая для этого списка: вставляем по месту. В хвост не кладём, если дальше есть ещё страницы —
  // она подтянется при прокрутке сама.
  const merged = [...list, next].sort(byActivity);
  const pos = merged.findIndex((c) => c.id === id);
  if (pos === merged.length - 1 && hasNextPage(data)) return unchanged;
  return commit(merged, total === null ? null : total + 1, false);
}

function commit(list: InboxConversation[], total: number | null, found: boolean): PatchResult {
  return { data: rebuild(list, total), unknown: false, changed: true, found };
}

/** Значок вкладки: сколько переписок с непрочитанным в наборе. */
export function countUnread(lists: Array<ListData | undefined>): number {
  const seen = new Set<number>();
  for (const d of lists) {
    for (const c of flatten(d)) {
      if (c.unreadCount > 0 && c.status === 'open') seen.add(c.id);
    }
  }
  return seen.size;
}
